import type { ReservationFlagDTO, ReservationFlagTypeEnum } from '@api/ringil-api.v2.tsx';

/**
 * Cross dock (and any future unit-bearing flag) declares how much of the booked cargo is handled that
 * way, so its `units` may never exceed what the reservation actually carries.
 *
 * The two client edit paths cannot see both halves of the pair: `PUT /reservation/{id}/flags` sends
 * flags without orders, and `POST /reservation/{id}/update-orders-timeslot` sends orders without
 * flags. Each path therefore validates against whatever it does have, with this module as the single
 * place the rule lives.
 */

/**
 * The part of an order both sides agree on: `GlobalOrderResponse` on detail, `Order` in the forms.
 * `discrepancyAmount` is what was actually handled; the discrepancy sidebar's text inputs leave it a
 * string, hence the coercion in `effectiveItemUnits`.
 */
export interface UnitCountableOrder {
  items?: { amount?: number; discrepancyAmount?: number | string }[];
}

/**
 * Flags whose `units` are capped by the booked cargo. Cross dock is the only one today — extend this
 * list rather than adding the check at a call site.
 *
 * Typed through a type-only import so this module stays free of the generated API client (and of
 * React with it), which `vitest.config.ts` could not load in its `node` environment.
 */
export const CROSS_DOCK = 'CROSS_DOCK' as ReservationFlagTypeEnum;

export const UNIT_CAPPED_FLAGS: readonly ReservationFlagTypeEnum[] = [CROSS_DOCK];

export const isUnitCappedFlag = (flagType?: ReservationFlagTypeEnum): boolean => UNIT_CAPPED_FLAGS.includes(flagType);

const toUnits = (value: number | string | null | undefined): number | undefined => (value == null || value === '' ? undefined : Number(value));

/** What an item actually carries: the recorded discrepancy when there is one, the booked amount otherwise. */
export const effectiveItemUnits = (item: { amount?: number; discrepancyAmount?: number | string }): number =>
  toUnits(item.discrepancyAmount) ?? item.amount ?? 0;

/** What a flag actually covers: the recorded discrepancy when there is one, the planned units otherwise. */
export const effectiveFlagUnits = (flag?: ReservationFlagDTO): number | undefined => flag?.discrepancyUnits ?? flag?.units;

/**
 * Total manipulation units on the reservation, aggregated the same way the reservation list reports them.
 * Uses each item's effective amount, matching the backend's check of the cross dock cap.
 */
export const totalOrderUnits = (orders?: UnitCountableOrder[]): number =>
  (orders ?? []).flatMap(order => order.items ?? []).reduce((sum, item) => sum + effectiveItemUnits(item), 0);

export interface ExceededUnitLimit {
  reservationFlagType: ReservationFlagTypeEnum;
  units: number;
  maxUnits: number;
}

/**
 * The capped flags whose effective units are above the effective order total.
 *
 * `orders` left undefined means the caller has no order data in hand (the create sidebar run with
 * orders hidden, for instance) — the cap is unknown there, so nothing is reported rather than
 * everything being measured against a total of zero. An empty `units` is the "required" rule's
 * business, not ours.
 */
export const findExceededUnitLimits = (flags?: ReservationFlagDTO[], orders?: UnitCountableOrder[]): ExceededUnitLimit[] => {
  if (orders === undefined) return [];

  const maxUnits = totalOrderUnits(orders);

  return (flags ?? [])
    .filter(flag => isUnitCappedFlag(flag?.reservationFlagType) && effectiveFlagUnits(flag) != null && effectiveFlagUnits(flag) > maxUnits)
    .map(flag => ({ reservationFlagType: flag.reservationFlagType, units: effectiveFlagUnits(flag), maxUnits }));
};

type Translate = (key: string, defaultText?: string, data?: any, count?: number) => string;

/** One wording for the breach, shared by the yup schema and the inline tag editor. */
export const unitLimitErrorMessage = (tr: Translate, maxUnits: number): string =>
  tr('validation.flagUnitsExceedOrderUnits', `Cannot exceed {{maxUnits}} units booked in the orders`, { maxUnits });

export type CrossDockDiscrepancyError = 'negative' | 'required' | 'exceedsOrders';

/**
 * Mirrors the backend check of the discrepancy sidebar (`reservation.flag.cross_dock_discrepancy_*`):
 * the actual cross dock (`discrepancyUnits ?? plannedUnits`) may not exceed the actual order total.
 * Once the orders drop below the planned cross dock, the user has to state the actual cross dock.
 */
export const findCrossDockDiscrepancyError = (
  plannedUnits: number | undefined,
  discrepancyUnits: number | undefined,
  orders: UnitCountableOrder[] | undefined,
): CrossDockDiscrepancyError | undefined => {
  if (discrepancyUnits != null && discrepancyUnits < 0) return 'negative';
  const actualOrders = totalOrderUnits(orders);
  if (discrepancyUnits == null) return plannedUnits != null && plannedUnits > actualOrders ? 'required' : undefined;
  return discrepancyUnits > actualOrders ? 'exceedsOrders' : undefined;
};

export const crossDockDiscrepancyErrorMessage = (tr: Translate, error: CrossDockDiscrepancyError, maxUnits: number): string => {
  switch (error) {
    case 'negative':
      return tr('validation.crossDockDiscrepancyNegative', 'Actual cross dock units cannot be negative');
    case 'required':
      return tr('validation.crossDockDiscrepancyRequired', 'Orders now total {{maxUnits}} units, below the planned cross dock. Fill in the actual cross dock units', {
        maxUnits,
      });
    case 'exceedsOrders':
      return tr('validation.crossDockDiscrepancyExceedsOrders', 'Cannot exceed {{maxUnits}} units actually in the orders', { maxUnits });
  }
};

/**
 * Flags as create and `PUT /reservation/{id}/flags` accept them. `discrepancyUnits` is set only through
 * the discrepancy sidebar (`reservation.flag.discrepancy_not_editable_here` otherwise), yet the flag
 * editors reuse the detail's flag objects and would echo it back.
 */
export const toEditableFlags = (flags?: ReservationFlagDTO[]): ReservationFlagDTO[] | undefined =>
  flags?.map(({ reservationFlagType, units }) => ({ reservationFlagType, units }));

/** Planned units, followed by the actual ones once recorded: `10 -> 7` (with an arrow glyph). */
export const flagUnitsText = (flag: ReservationFlagDTO): string | undefined => {
  if (flag.units == null && flag.discrepancyUnits == null) return undefined;
  if (flag.discrepancyUnits == null) return `${flag.units}`;
  return `${flag.units ?? '\u2013'} \u2192 ${flag.discrepancyUnits}`;
};
